import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Users,
  DollarSign,
  Camera,
  TrendingUp,
  Eye,
  ShoppingCart,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@context/AuthContext';
import { usePhotographer } from '@context/PhotographerContext';
import { photographerAPI } from '@services/photographerApi';
import DashboardCard from '../components/DashboardCard';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import Alert from '@components/common/Alert';
import OnboardingWizard from '../OnboardingWizard';
import type { PhotographerAnalytics, OnboardingStatus } from '@/types/photographer';
import { buildGalleryBaseUrl } from '@utils/galleryUrl';

interface DashboardHomeProps {
  onboardingStatus?: OnboardingStatus | null;
  onboardingLoading?: boolean;
}

const DashboardHome: React.FC<DashboardHomeProps> = ({
  onboardingStatus,
  onboardingLoading: providedOnboardingLoading = false,
}) => {
  const { user } = useAuth();
  const { photographer } = usePhotographer();
  const [analytics, setAnalytics] = useState<PhotographerAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null);
  const [localOnboardingLoading, setLocalOnboardingLoading] = useState(true);
  const hasProvidedOnboardingStatus = onboardingStatus !== undefined;
  const effectiveOnboarding = hasProvidedOnboardingStatus ? onboardingStatus : onboarding;
  const effectiveOnboardingLoading = hasProvidedOnboardingStatus
    ? providedOnboardingLoading
    : localOnboardingLoading;

  useEffect(() => {
    if (hasProvidedOnboardingStatus) return;
    if (!photographer) return;
    let active = true;
    photographerAPI
      .getOnboardingStatus()
      .then(res => {
        if (active) setOnboarding(res.data);
      })
      .catch(() => {
        /* treat as complete on error so users aren't stranded */
      })
      .finally(() => {
        if (active) setLocalOnboardingLoading(false);
      });
    return () => {
      active = false;
    };
  }, [photographer, hasProvidedOnboardingStatus]);

  const onboardingIncomplete =
    effectiveOnboarding !== null && effectiveOnboarding.completed < effectiveOnboarding.total;

  useEffect(() => {
    if (onboardingIncomplete) {
      setLoading(false);
      return;
    }
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch last 30 days analytics
        const response = await photographerAPI.getAnalytics({
          period: 'month',
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString(),
        });

        setAnalytics(response.data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch analytics');
      } finally {
        setLoading(false);
      }
    };

    if (photographer) {
      fetchAnalytics();
    }
  }, [photographer, onboardingIncomplete]);

  if (effectiveOnboardingLoading || (loading && !onboardingIncomplete)) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (onboardingIncomplete && effectiveOnboarding) {
    return <OnboardingWizard status={effectiveOnboarding} firstName={user?.first_name} />;
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert type="error" title="Failed to load dashboard" message={error} />
      </div>
    );
  }

  const dashboardStats = [
    {
      name: 'Total Revenue',
      value: analytics?.summary.totalRevenue || 0,
      change: { value: 15, trend: 'up' as const, period: 'last month' },
      icon: DollarSign,
    },
    {
      name: 'Total Orders',
      value: analytics?.summary.totalOrders || 0,
      change: { value: 8, trend: 'up' as const, period: 'last month' },
      icon: ShoppingCart,
    },
    {
      name: 'Active Customers',
      value: analytics?.summary.totalCustomers || 0,
      change: { value: 12, trend: 'up' as const, period: 'last month' },
      icon: Users,
    },
    {
      name: 'Average Order Value',
      value: `$${analytics?.summary.averageOrderValue?.toFixed(2) || '0.00'}`,
      change: { value: 5, trend: 'up' as const, period: 'last month' },
      icon: TrendingUp,
    },
  ];
  const galleryBaseUrl = buildGalleryBaseUrl(photographer);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {photographer?.contactInfo.name || 'Photographer'}
          </h1>
          <p className="text-gray-600">Here's what's happening with your photography business</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            icon={<Eye className="h-4 w-4" />}
            disabled={!galleryBaseUrl}
            onClick={() => {
              if (galleryBaseUrl) {
                window.open(galleryBaseUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            View Gallery
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            as={Link}
            to="/dashboard/events"
          >
            Create Event
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map(stat => (
          <DashboardCard
            key={stat.name}
            title={stat.name}
            value={stat.value}
            change={stat.change}
            icon={stat.icon}
          />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Recent Events */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Events</h3>
            <Link to="/dashboard/events" className="text-sm text-blue-600 hover:text-blue-500">
              View all
            </Link>
          </div>

          <div className="space-y-3">
            {analytics?.summary.topEvents?.slice(0, 3).map(event => (
              <div key={event.eventId} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{event.title}</p>
                  <p className="text-xs text-gray-500">{event.views} views</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">${event.revenue.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{event.orders} orders</p>
                </div>
              </div>
            )) || (
              <div className="text-center py-4">
                <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No events yet</p>
                <Button
                  variant="outline"
                  size="small"
                  className="mt-2"
                  as={Link}
                  to="/dashboard/events"
                >
                  Create your first event
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Top Selling Photos */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Top Selling Photos</h3>
            <Link to="/dashboard/analytics" className="text-sm text-blue-600 hover:text-blue-500">
              View analytics
            </Link>
          </div>

          <div className="space-y-3">
            {analytics?.summary.topSellingPhotos?.slice(0, 3).map(photo => (
              <div key={photo.photoId} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 truncate">{photo.title}</p>
                  <p className="text-xs text-gray-500">{photo.sales} sales</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">${photo.revenue.toFixed(2)}</p>
                </div>
              </div>
            )) || (
              <div className="text-center py-4">
                <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No photo sales yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Business Health</h3>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Conversion Rate</span>
                <span className="font-medium text-gray-900">
                  {(analytics?.summary.conversionRate || 0).toFixed(1)}%
                </span>
              </div>
              <div className="mt-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{
                    width: `${Math.min((analytics?.summary.conversionRate || 0) * 10, 100)}%`,
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Subscription Status</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Next Billing</span>
                <span className="font-medium text-gray-900">
                  {photographer?.subscriptionPlan === 'basic'
                    ? '$29'
                    : photographer?.subscriptionPlan === 'pro'
                      ? '$59'
                      : '$99'}
                </span>
              </div>
            </div>

            <Button
              variant="outline"
              size="small"
              className="w-full mt-4"
              as={Link}
              to="/dashboard/billing"
            >
              Manage Billing
            </Button>
          </div>
        </div>
      </div>

      {/* Recent Activity or Getting Started */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Getting Started</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 border border-gray-200 rounded-lg">
            <Calendar className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-gray-900 mb-1">Create Your First Event</h4>
            <p className="text-xs text-gray-500 mb-3">
              Start by creating an event and uploading photos
            </p>
            <Button variant="primary" size="small" as={Link} to="/dashboard/events">
              Create Event
            </Button>
          </div>

          <div className="text-center p-4 border border-gray-200 rounded-lg">
            <Eye className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-gray-900 mb-1">Customize Your Gallery</h4>
            <p className="text-xs text-gray-500 mb-3">
              Personalize your gallery with your branding
            </p>
            <Button variant="outline" size="small" as={Link} to="/dashboard/settings/branding">
              Customize
            </Button>
          </div>

          <div className="text-center p-4 border border-gray-200 rounded-lg">
            <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-gray-900 mb-1">Share Your Gallery</h4>
            <p className="text-xs text-gray-500 mb-3">Share your gallery link with clients</p>
            <Button
              variant="outline"
              size="small"
              disabled={!galleryBaseUrl}
              onClick={() => {
                if (galleryBaseUrl) {
                  navigator.clipboard.writeText(galleryBaseUrl);
                  toast.success('Gallery link copied to clipboard');
                }
              }}
            >
              Copy Link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
